/**
 * Builds "report an issue" links pointing at the public repository's issue
 * tracker.
 *
 * The app has no backend of its own, so bug reports are filed as GitHub issues
 * on the public mirror. Rather than dropping the user on an empty issue form,
 * every entry point prefills the body with the diagnostics we would otherwise
 * have to ask for: which deployment they were on, which page they were on,
 * their browser, and — for crashes — the stack trace.
 *
 * Nothing identifying the reporter or their work goes into the body. The
 * tracker is public and the reporter's project may not be, so no project or
 * account id is included, and Jazz ids are stripped out of the page URL rather
 * than carried in by the back door. A maintainer who needs the project asks
 * for it in the issue thread.
 *
 * The prefilled body is only a suggestion: GitHub shows it in an editable
 * textarea, so the user always sees and can remove anything before submitting.
 * Nothing is transmitted by building or opening the URL.
 */

/** Issue tracker the reports are filed against. */
const ISSUE_TRACKER_URL =
  "https://github.com/sakari/vibediagram-public/issues/new";

/**
 * Upper bound for the generated URL.
 *
 * GitHub rejects requests whose URL exceeds roughly 8KB. Staying well under
 * that leaves room for the user agent and page URL, which we cannot shorten,
 * and for proxies that impose lower limits of their own.
 */
const MAX_URL_LENGTH = 6000;

/** Marker appended to a stack trace that had to be shortened to fit the URL. */
export const TRUNCATION_MARKER = "\n… (truncated)";

/**
 * Longest error message used as an issue title. Titles beyond this are
 * unreadable in the tracker list, and an unbounded one would eat the URL
 * budget that the stack trace needs.
 */
const MAX_TITLE_LENGTH = 160;

/** Facts about the running app, identical for every report from a session. */
export interface ReportEnvironment {
  /** Vercel deployment id, or "local" for a development build. */
  readonly deploymentId: string;
  /** Page the user was on when they chose to report. */
  readonly pageUrl: string;
  /** Browser user agent string. */
  readonly userAgent: string;
}

/** What went wrong, for a report raised from a crash. */
export interface ReportDetails {
  /**
   * Message of a caught crash, used as the issue title so similar reports are
   * recognisable in the tracker list. Absent for a manually filed report,
   * which leaves the title empty for the user to write.
   */
  readonly message?: string;
  /**
   * Stack trace of a caught crash. Present only for reports raised from the
   * error boundary; a manually filed report has nothing to attach.
   */
  readonly stack?: string;
}

/**
 * Matches a Jazz id — project, account, or any other CoValue — wherever it
 * appears in a URL, so ids can be removed from the reported page location.
 */
const JAZZ_ID_PATTERN = /co_z[A-Za-z0-9]+/g;

/**
 * Replaces Jazz ids in a URL with a placeholder.
 *
 * The workspace route embeds the open project's id, so reporting the raw
 * `window.location.href` would publish exactly the identifier the report is
 * meant to leave out. The route itself is still worth knowing, so the shape of
 * the URL is kept and only the ids are elided.
 */
export function redactPageUrl(pageUrl: string): string {
  return pageUrl.replace(JAZZ_ID_PATTERN, "co_…");
}

/** Reads the environment facts from the running browser. */
function currentEnvironment(): ReportEnvironment {
  return {
    deploymentId: __DEPLOYMENT_ID__,
    pageUrl: window.location.href,
    userAgent: window.navigator.userAgent,
  };
}

/** Renders the diagnostics list shown under the user's own description. */
function diagnosticsSection(environment: ReportEnvironment): string {
  return [
    `- Deployment: \`${environment.deploymentId}\``,
    `- Page: ${redactPageUrl(environment.pageUrl)}`,
    `- Browser: ${environment.userAgent}`,
  ].join("\n");
}

/** Renders the whole issue body, with the stack trace last so it can be cut. */
function issueBody(
  environment: ReportEnvironment,
  stack: string | undefined,
): string {
  const sections = [
    "## What happened\n\n<!-- Describe what you did and what you expected instead. -->",
    `## Diagnostics\n\n${diagnosticsSection(environment)}`,
  ];
  if (stack !== undefined) {
    sections.push("## Stack trace\n\n```\n" + stack + "\n```");
  }
  return sections.join("\n\n");
}

/** Assembles the tracker URL for a body and title. */
function trackerUrl(title: string, body: string): string {
  const params = new URLSearchParams({ title, body, labels: "bug" });
  return `${ISSUE_TRACKER_URL}?${params.toString()}`;
}

/**
 * Builds a URL that opens GitHub's new-issue form prefilled with the report.
 *
 * The returned URL is always at most `MAX_URL_LENGTH` characters: a stack
 * trace long enough to push it over is truncated from the end, since the
 * frames nearest the throw are the informative ones. Diagnostics are never
 * dropped — they are short, and a report without them is the thing we are
 * trying to avoid.
 *
 * `environment` is injected so the function stays pure and testable; callers
 * in the app omit it and get the live browser values.
 */
export function buildIssueUrl(
  details: ReportDetails,
  environment: ReportEnvironment = currentEnvironment(),
): string {
  const title =
    details.message === undefined
      ? ""
      : `Crash: ${details.message}`.slice(0, MAX_TITLE_LENGTH);
  const full = trackerUrl(title, issueBody(environment, details.stack));
  if (full.length <= MAX_URL_LENGTH || details.stack === undefined) {
    // With no stack there is nothing left to shorten. An over-long URL here
    // would need an absurd user agent or page URL, and a truncated diagnostics
    // block is worse than a URL GitHub might reject.
    return full;
  }

  // Binary search the longest stack prefix that still fits. The body is
  // URL-encoded, so the relationship between stack length and URL length is
  // not one-to-one and cannot be subtracted directly.
  let fits = 0;
  let tooLong = details.stack.length;
  while (fits < tooLong) {
    const midpoint = Math.ceil((fits + tooLong) / 2);
    const candidate = details.stack.slice(0, midpoint) + TRUNCATION_MARKER;
    const url = trackerUrl(title, issueBody(environment, candidate));
    if (url.length <= MAX_URL_LENGTH) {
      fits = midpoint;
    } else {
      tooLong = midpoint - 1;
    }
  }
  return trackerUrl(
    title,
    issueBody(environment, details.stack.slice(0, fits) + TRUNCATION_MARKER),
  );
}
